import { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import SEOHead from '../../components/SEO/SEOHead';
import SEOFooter from '../../components/SEO/SEOFooter';
import { ArrowLeft, ChevronRight } from 'lucide-react';

const PalpitesIAPage = () => {
  const navigate = useNavigate();
  const [expandedFaq, setExpandedFaq] = useState(null);

  const esportesCobertos = [
    {
      esporte: 'Futebol',
      tipos: 'Resultado, Gols, Escanteios, Cartões',
      diferencial: 'Consulta notícias para fatores de momentum. Peça ao PickStar para construir modelos ML personalizados de acordo com sua preferência'
    },
    {
      esporte: 'Brasileirão',
      tipos: 'Série A, Série B, Estaduais',
      diferencial: 'Consulta notícias para fatores de momentum. Peça ao PickStar para construir modelos ML personalizados de acordo com sua preferência'
    },
    {
      esporte: 'Libertadores',
      tipos: 'Mata-mata, Fase de Grupos',
      diferencial: 'Consulta notícias para fatores de momentum. Peça ao PickStar para construir modelos ML personalizados de acordo com sua preferência'
    },
    {
      esporte: 'Copa do Brasil',
      tipos: 'Ida e Volta, Gol Fora',
      diferencial: 'Consulta notícias para fatores de momentum. Peça ao PickStar para construir modelos ML personalizados de acordo com sua preferência'
    }
  ];

  const funcionalidadesIA = [
    {
      titulo: 'Processamento em Tempo Real',
      descricao: 'Analisa estatísticas ao vivo, lesões, clima e movimentos do mercado em segundos'
    },
    {
      titulo: 'Modelos Profissionais',
      descricao: 'Criado por engenheiros de ML que são apostadores profissionais com histórico comprovado'
    },
    {
      titulo: 'Detecção de Value Bets',
      descricao: 'Identifica odds mal precificadas onde as casas subestimam as verdadeiras probabilidades'
    },
    {
      titulo: 'Expertise Multi-Esportes',
      descricao: 'Modelos especializados para Futebol, Basquete, Baseball, MMA e mais'
    }
  ];

  const porqueMelhorQue = [
    {
      vs: 'Tipsters Tradicionais',
      vantagem: 'Pesquisa quantitativa avançada, sem emoção ou viés'
    },
    {
      vs: 'ChatGPT',
      vantagem: 'Modelos quantitativos especializados em apostas, não genérico'
    },
    {
      vs: 'Grupos do Telegram',
      vantagem: 'Metodologia quantitativa transparente, sem cherry-picking'
    },
    {
      vs: 'Intuição Própria',
      vantagem: 'Análise quantitativa de 1000x mais dados em segundos'
    }
  ];

  const howItWorks = [
    {
      step: '1',
      title: 'Fine-Tuning e Conexões API em Tempo Real',
      description: 'Agentes com aprendizado refinado conectam-se a APIs em tempo real para dados ao vivo'
    },
    {
      step: '2',
      title: 'Sumarização Automatizada de Alta e Baixa Frequência',
      description: 'Técnicas automatizadas processam informações em múltiplas frequências temporais'
    },
    {
      step: '3',
      title: 'Análise e Avaliação Multi-Ferramenta',
      description: 'Ferramentas especializadas trabalham em conjunto para análise profunda'
    },
    {
      step: '4',
      title: 'Avaliação e Validação Cruzada',
      description: 'Métodos de validação cruzada garantem robustez das análises'
    },
    {
      step: '5',
      title: 'Previsão com Confiança e Evidências',
      description: 'Você recebe previsões com níveis de confiança, evidências de suporte e pesquisas detalhadas que pode explorar ou seguir diretamente'
    }
  ];

  const faqs = [
    {
      q: "O que são palpites da IA?",
      a: "Palpites da IA são previsões geradas por modelos de inteligência artificial que analisam milhões de dados esportivos, estatísticas, tendências, lesões e mercados de apostas para identificar as oportunidades mais lucrativas."
    },
    {
      q: "Posso usar para fazer a fezinha?",
      a: "Sim! Nossa IA é perfeita para 'fazer a fezinha' com segurança. Te ajudamos a encontrar os melhores palpites mesmo com pouco dinheiro, maximizando suas chances."
    },
    {
      q: "Como o PickStar AI é diferente do ChatGPT?",
      a: "ChatGPT é genérico. PickStar AI foi construído por engenheiros de ML que são apostadores profissionais, com contexto especializado em apostas esportivas. Processamos dados em tempo real e usamos modelos treinados em milhões de apostas."
    },
    {
      q: "Funciona para apostas ao vivo?",
      a: "Absolutamente. Nossa IA se destaca em apostas ao vivo processando stats do jogo e mudanças de momentum em tempo real, encontrando value que as casas não veem durante a partida."
    }
  ];

  // Structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Palpites da IA: Robô de Apostas Esportivas Grátis",
    "description": "Palpites certeiros de IA para Brasileirão, Libertadores, Copa do Brasil. Robô de apostas grátis com 67%+ de precisão. Inteligência artificial para futebol.",
    "author": {
      "@type": "Organization",
      "name": "PickStar AI"
    },
    "publisher": {
      "@type": "Organization",
      "name": "PickStar AI",
      "logo": {
        "@type": "ImageObject",
        "url": "https://pickstarai.com/logo.png"
      }
    },
    "datePublished": "2025-10-23",
    "dateModified": new Date().toISOString(),
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://pickstarai.com/palpites-ia"
    }
  };

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a
      }
    }))
  };

  return (
    <>
      <SEOHead
        title="Palpites da IA Grátis | Robô de Apostas Esportivas com Inteligência Artificial"
        description="Palpites certeiros da IA para Brasileirão, Libertadores, Copa do Brasil. Robô de apostas grátis com 67%+ de precisão. App de inteligência artificial para futebol e apostas esportivas."
        keywords="palpites ia, palpites da ia, robô de palpites de futebol grátis, inteligência artificial apostas futebol, app apostas inteligência artificial, robô de apostas esportivas grátis, palpites certeiros, ia futebol"
        canonical="https://pickstarai.com/palpites-ia"
        ogImage="https://pickstarai.com/og-home-logo.png"
        structuredData={[structuredData, faqStructuredData]}
      />

      <div className="min-h-screen bg-black">
        {/* Glass layer background */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/[0.02] to-transparent" />

        {/* Terminal-style Header */}
        <header className="fixed top-0 z-50 w-full bg-black border-b border-white/[0.05]">
          <div className="max-w-screen-xl mx-auto px-6 py-3">
            <div className="flex items-center justify-between">
              <Link
                to="/"
                className="flex items-center text-[11px] text-white/50 hover:text-white/80 transition-all uppercase tracking-[0.15em]"
              >
                <ArrowLeft className="w-3 h-3 mr-2" />
                VOLTAR
              </Link>
              <div className="text-[9px] text-white/30 uppercase tracking-[0.2em]">
                ATUALIZADO: {new Date().toLocaleDateString('pt-BR').toUpperCase()}
              </div>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="min-h-[60vh] flex items-center justify-center px-8 pt-20 pb-12">
          <div className="text-center max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="mb-6"
            >
              <h1 className="text-5xl lg:text-7xl font-thin text-white/95 leading-none mb-2">
                Palpites da IA
              </h1>
              <p className="text-xs text-white/50 uppercase tracking-[0.2em] mt-4">
                Robô de apostas esportivas grátis com inteligência artificial
              </p>
            </motion.div>
            <div className="mt-8">
              <button
                onClick={() => navigate('/chat')}
                className="inline-flex items-center justify-center px-8 py-3 bg-gradient-to-r from-emerald-500/80 to-violet-500/80
                         text-[11px] font-medium text-white uppercase tracking-[0.15em]
                         hover:from-emerald-500 hover:to-violet-500 transition-all"
              >
                COMEÇAR GRÁTIS
                <ChevronRight className="ml-2 w-3 h-3" />
              </button>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <div className="relative z-10 max-w-screen-xl mx-auto px-6 py-12">

          {/* Esportes Cobertos */}
          <section className="mb-16 pb-12 border-b border-white/[0.05]">
            <h2 className="text-2xl font-light text-white/95 mb-6">
              <span className="text-[11px] text-white/30 uppercase tracking-[0.3em] block mb-1">COBERTURA</span>
              ESPORTES QUE COBRIMOS
            </h2>

            <div className="grid md:grid-cols-2 gap-4">
              {esportesCobertos.map((esporte, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white/[0.02] border border-white/[0.05] hover:border-white/10 p-6 transition-all"
                >
                  <h3 className="text-lg font-medium text-white/90 mb-4">{esporte.esporte}</h3>

                  <div className="space-y-3 text-sm">
                    <div>
                      <span className="text-[10px] text-white/40 uppercase tracking-wider block mb-1">Tipos de Palpite:</span>
                      <p className="text-white/60 text-xs">{esporte.tipos}</p>
                    </div>
                    <div>
                      <span className="text-[10px] text-white/40 uppercase tracking-wider block mb-1">Diferencial da IA:</span>
                      <p className="text-white/50 text-xs">{esporte.diferencial}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </section>

          {/* Funcionalidades da IA */}
          <section className="mb-16 pb-12 border-b border-white/[0.05]">
            <h2 className="text-2xl font-light text-white/95 mb-6">
              <span className="text-[11px] text-white/30 uppercase tracking-[0.3em] block mb-1">DIFERENCIAIS</span>
              O QUE TORNA NOSSA IA DIFERENTE
            </h2>

            <div className="grid md:grid-cols-2 gap-4">
              {funcionalidadesIA.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white/[0.02] border border-white/[0.05] hover:border-white/10 p-6 transition-all"
                >
                  <h3 className="text-sm text-white/80 uppercase tracking-[0.15em] font-medium mb-3">
                    {feature.titulo}
                  </h3>
                  <p className="text-sm text-white/50">{feature.descricao}</p>
                </motion.div>
              ))}
            </div>
          </section>

          {/* Por Que Melhor */}
          <section className="mb-16 pb-12 border-b border-white/[0.05]">
            <h2 className="text-2xl font-light text-white/95 mb-6">
              <span className="text-[11px] text-white/30 uppercase tracking-[0.3em] block mb-1">COMPARAÇÃO</span>
              PICKSTAR IA VS. CONCORRÊNCIA
            </h2>

            <div className="space-y-0 border border-white/[0.05]">
              {porqueMelhorQue.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between px-6 py-4 hover:bg-white/[0.02] transition-colors border-b border-white/[0.05] last:border-b-0"
                >
                  <div className="flex-1">
                    <p className="text-sm font-mono text-white/60 mb-1">vs. {item.vs}</p>
                    <p className="text-xs text-white/50">{item.vantagem}</p>
                  </div>
                  <span className="text-emerald-400/80 text-xl">✓</span>
                </div>
              ))}
            </div>
          </section>

          {/* Como Funciona */}
          <section className="mb-16 pb-12 border-b border-white/[0.05]">
            <h2 className="text-2xl font-light text-white/95 mb-6">
              <span className="text-[11px] text-white/30 uppercase tracking-[0.3em] block mb-1">METODOLOGIA</span>
              COMO FUNCIONA O ROBÔ DE PALPITES
            </h2>

            <div className="space-y-4">
              {howItWorks.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white/[0.02] border border-white/[0.05] hover:border-white/10 p-6 transition-all"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 border border-emerald-500/20 flex items-center justify-center">
                        <span className="text-emerald-400/80 font-mono text-sm">{step.step}</span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm text-white/80 uppercase tracking-[0.15em] font-medium mb-3">
                        {step.title}
                      </h3>
                      <p className="text-sm text-white/50">{step.description}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </section>

          {/* FAQ Section */}
          <section className="mb-16">
            <h2 className="text-2xl font-light text-white/95 mb-6">
              <span className="text-[11px] text-white/30 uppercase tracking-[0.3em] block mb-1">FAQ</span>
              PERGUNTAS FREQUENTES
            </h2>

            <div className="space-y-0 border border-white/[0.05]">
              {faqs.map((faq, index) => (
                <details key={index} className="group">
                  <summary className="px-6 py-4 cursor-pointer flex justify-between items-center hover:bg-white/[0.02] transition-colors border-b border-white/[0.05] last:border-b-0">
                    <span className="text-sm text-white/80 font-medium">{faq.q}</span>
                    <ChevronRight className="w-4 h-4 text-white/40 group-open:rotate-90 transition-transform" />
                  </summary>
                  <div className="px-6 py-4 text-sm text-white/60 leading-relaxed border-b border-white/[0.05] last:border-b-0">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </section>

          {/* CTA Section */}
          <section className="text-center py-12 border-t border-white/[0.05]">
            <h2 className="text-2xl font-light text-white/95 mb-4">
              <span className="text-[11px] text-white/30 uppercase tracking-[0.3em] block mb-2">COMEÇAR</span>
              COMECE A GANHAR COM PALPITES DA IA HOJE
            </h2>
            <p className="text-sm text-white/50 mb-8 max-w-2xl mx-auto">
              Junte-se a milhares de apostadores inteligentes usando o PickStar IA para decisões baseadas em dados.
            </p>
            <button
              onClick={() => navigate('/chat')}
              className="inline-flex items-center justify-center px-10 py-4 bg-gradient-to-r from-emerald-500/80 to-violet-500/80
                       text-sm font-medium text-white uppercase tracking-[0.15em]
                       hover:from-emerald-500 hover:to-violet-500 transition-all"
            >
              PEGAR PALPITES GRÁTIS AGORA
              <ChevronRight className="ml-2 w-4 h-4" />
            </button>
          </section>
        </div>

        {/* Footer */}
        <div className="border-t border-white/[0.05]">
          <SEOFooter language="pt-BR" />
        </div>
      </div>
    </>
  );
};

export default PalpitesIAPage;
